import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';
import { newCalculationStarted } from './historySlice';
import { getInitialXyFields } from 'utils/schemaUtils';

interface LocationTypeSetPayload {
  locationType: LocationType;
  numericFields: string[];
}

const initialState: KeyFields = {
  geometry: {
    locationType: undefined
  },
  dateTime: {
    hasDateTime: undefined
  },
  trackingField: undefined
};

const resetKeyFields = (state: KeyFields) => {
  state.geometry = {
    locationType: undefined
  };
  state.dateTime = {
    hasDateTime: undefined
  };
  state.trackingField = undefined;
};

export const counterSlice = createSlice({
  name: 'keyFields',
  initialState,
  reducers: {
    initGeometry: (state, action: PayloadAction<GeometryFields>) => {
      state.geometry = action.payload;
    },
    locationTypeSet: (state, action: PayloadAction<LocationTypeSetPayload>) => {
      state.geometry.locationType = action.payload.locationType;
      delete state.geometry.geometryField;
      delete state.geometry.xField;
      delete state.geometry.yField;
      delete state.geometry.zField;
      delete state.geometry.geometryType;
      delete state.geometry.geometryFieldFormat;
      delete state.geometry.spatialReference;
      if (action.payload.locationType !== '__XY__') return;
      const numericFields = action.payload.numericFields;
      const { x, y } = getInitialXyFields(numericFields);
      state.geometry.xField = x;
      state.geometry.yField = y;
      state.geometry.geometryType = 'esriGeometryPoint';
      state.geometry.spatialReference = { wkid: 4326 };
    },
    singleGeometryFieldSet: (state, action: PayloadAction<string>) => {
      state.geometry.geometryField = action.payload;
    },
    xFieldSet: (state, action: PayloadAction<string>) => {
      state.geometry.xField = action.payload;
    },
    yFieldSet: (state, action: PayloadAction<string>) => {
      state.geometry.yField = action.payload;
    },
    zFieldSet: (state, action: PayloadAction<string>) => {
      state.geometry.zField = action.payload;
    },
    geometryTypeSet: (state, action: PayloadAction<GeometryType>) => {
      state.geometry.geometryType = action.payload;
      if (state.geometry.geometryType === '__NONE__') {
        delete state.geometry.spatialReference;
      }
    },
    geometryFormatSet: (state, action: PayloadAction<string>) => {
      state.geometry.geometryFieldFormat = action.payload;
    },
    wkidSet: (state, action: PayloadAction<number>) => {
      state.geometry.spatialReference = { wkid: action.payload };
    },
    hasDateTimeSet: (state, action: PayloadAction<boolean>) => {
      state.dateTime = {
        hasDateTime: action.payload
      };
    },
    dateFormatSet: (state, action: PayloadAction<string>) => {
      state.dateTime.dateFormat = action.payload;
    },
    dateFormattingStringSet: (state, action: PayloadAction<string>) => {
      state.dateTime.dateFormattingString = action.payload;
    },
    startTimeFieldSet: (state, action: PayloadAction<string>) => {
      state.dateTime.startTimeField = action.payload;
    },
    endTimeFieldSet: (state, action: PayloadAction<string>) => {
      state.dateTime.endTimeField = action.payload;
    },
    trackingFieldSet: (state, action: PayloadAction<string>) => {
      state.trackingField = action.payload;
    },
    keyFieldsReset: resetKeyFields
  },
  extraReducers: builder => {
    builder.addCase(newCalculationStarted, resetKeyFields);
  }
});

export const {
  initGeometry,
  locationTypeSet,
  xFieldSet,
  yFieldSet,
  zFieldSet,
  singleGeometryFieldSet,
  geometryTypeSet,
  geometryFormatSet,
  wkidSet,
  hasDateTimeSet,
  dateFormatSet,
  dateFormattingStringSet,
  startTimeFieldSet,
  endTimeFieldSet,
  trackingFieldSet,
  keyFieldsReset
} = counterSlice.actions;

export const locationTypeSelector = (state: RootState) =>
  state.keyFields.geometry.locationType;

export const xFieldSelector = (state: RootState) =>
  state.keyFields.geometry.xField;

export const yFieldSelector = (state: RootState) =>
  state.keyFields.geometry.yField;

export const zFieldSelector = (state: RootState) =>
  state.keyFields.geometry.zField;

export const singleGeometryFieldSelector = (state: RootState) =>
  state.keyFields.geometry.geometryField;

export const geometryFormatSelector = (state: RootState) =>
  state.keyFields.geometry.geometryFieldFormat;

export const geometryTypeSelector = (state: RootState) =>
  state.keyFields.geometry.geometryType;

export const wkidSelector = (state: RootState) =>
  state.keyFields.geometry.spatialReference?.wkid;

export const hasDateTimeSelector = (state: RootState) =>
  state.keyFields.dateTime.hasDateTime;

export const dateFormatSelector = (state: RootState) =>
  state.keyFields.dateTime.dateFormat;

export const dateFormattingStringSelector = (state: RootState) =>
  state.keyFields.dateTime.dateFormattingString;

export const startTimeFieldSelector = (state: RootState) =>
  state.keyFields.dateTime.startTimeField;

export const endTimeFieldSelector = (state: RootState) =>
  state.keyFields.dateTime.endTimeField;

export const trackingFieldSelector = (state: RootState) =>
  state.keyFields.trackingField;

const locationIsValidSelector = (state: RootState): boolean => {
  const geometry = state.keyFields.geometry;
  const { hasDerivedGeometry, formatName } = state.schema;

  // 1. Known location types don't require validation
  if (hasDerivedGeometry) return true;

  // 2. esri-json with missing location must be 'None' or include spatial reference
  if (formatName === 'esri-json') {
    const typeDefined = geometry.geometryType !== '__NONE__';
    return !typeDefined || !!geometry.spatialReference?.wkid;
  }

  // 3. geo-json with missing geometry needs user defined geometryType
  if (formatName === 'geo-json') {
    return !!geometry.geometryType;
  }

  // 4. Unknown location
  switch (geometry.locationType) {
    case '__NONE__':
      return true;
    case '__SINGLE__':
      return !!(
        geometry.geometryField &&
        geometry.geometryType &&
        geometry.geometryFieldFormat &&
        geometry.spatialReference?.wkid
      );
    case '__XY__':
      return !!(
        geometry.xField &&
        geometry.yField &&
        geometry.spatialReference?.wkid
      );
    default:
      return false;
  }
};

const dateTimeIsValidSelector = (state: RootState): boolean => {
  const dateTime = state.keyFields.dateTime;
  switch (dateTime.hasDateTime) {
    case false:
      return true;
    case true:
      if (dateTime.dateFormat === 'other') {
        return !!(dateTime.dateFormattingString && !!dateTime.startTimeField);
      } else if (dateTime.dateFormat === 'epoch') {
        return !!dateTime.startTimeField;
      } else {
        return false;
      }
    default:
      return false;
  }
};

export const isValidConfigSelector = createSelector(
  locationIsValidSelector,
  dateTimeIsValidSelector,
  (locationIsValid, dateTimeIsValid) => locationIsValid && dateTimeIsValid
);

export const keyFieldsSelector = (state: RootState) => state.keyFields;

export default counterSlice.reducer;
